/**
 * Provider marks and file-kind icons for cloud files. Simple inline SVG /
 * lucide shapes in neutral colours — no brand images are downloaded.
 */

import React from 'react';
import {
  Cloud,
  File,
  FileArchive,
  FileImage,
  FileSpreadsheet,
  FileText,
  FileVideo,
  Folder,
  Presentation,
  ClipboardList,
  PenTool,
} from 'lucide-react';
import type { CloudFileProvider } from '@/services/messagingApi';
import type { CloudFileKind } from './cloudLinks';
import { cn } from '@/lib/utils';

// Single-letter glyph inside the SharePoint mark (a logo, not prose).
const SHAREPOINT_GLYPH = 'S';

export function ProviderMark({ provider, className }: { provider: CloudFileProvider; className?: string }) {
  const cls = cn('h-4 w-4 flex-shrink-0', className);
  if (provider === 'google_drive') {
    // Drive's triangle, drawn as three strokes.
    return (
      <svg viewBox="0 0 24 24" className={cls} aria-hidden="true" data-provider-mark="google_drive">
        <path d="M8.5 3h7l6.5 11.5-3.5 6H5.5L2 14.5 8.5 3z" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
        <path d="M8.5 3l6.5 11.5H2M15.5 3L9 14.5l-3.5 6M22 14.5H9" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" opacity="0.7" />
      </svg>
    );
  }
  if (provider === 'sharepoint') {
    return (
      <svg viewBox="0 0 24 24" className={cls} aria-hidden="true" data-provider-mark="sharepoint">
        <circle cx="13" cy="8" r="5.5" fill="none" stroke="currentColor" strokeWidth="1.6" />
        <circle cx="16.5" cy="15.5" r="4" fill="none" stroke="currentColor" strokeWidth="1.4" opacity="0.7" />
        <rect x="2.5" y="7.5" width="10" height="10" rx="1.5" fill="currentColor" />
        <text x="7.5" y="15.4" textAnchor="middle" fontSize="8" fontWeight="700" fill="white" fontFamily="system-ui, sans-serif">{SHAREPOINT_GLYPH}</text>
      </svg>
    );
  }
  return <Cloud className={cls} aria-hidden="true" data-provider-mark="onedrive" />;
}

export function FileKindIcon({ kind, className }: { kind: CloudFileKind; className?: string }) {
  const cls = cn('h-4 w-4 flex-shrink-0', className);
  switch (kind) {
    case 'folder':
      return <Folder className={cls} />;
    case 'document':
      return <FileText className={cls} />;
    case 'spreadsheet':
      return <FileSpreadsheet className={cls} />;
    case 'presentation':
      return <Presentation className={cls} />;
    case 'form':
      return <ClipboardList className={cls} />;
    case 'drawing':
      return <PenTool className={cls} />;
    case 'pdf':
      return <FileText className={cls} />;
    case 'image':
      return <FileImage className={cls} />;
    case 'video':
      return <FileVideo className={cls} />;
    case 'archive':
      return <FileArchive className={cls} />;
    default:
      return <File className={cls} />;
  }
}

export function formatBytes(bytes: number | null | undefined): string | null {
  if (bytes == null || !Number.isFinite(bytes) || bytes <= 0) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}
